import React, { useState, useEffect, useRef } from 'react';
import { useScrollRestore } from '../hooks/useScrollRestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { useWebNotification } from '../hooks/useWebNotification';
import { ArrowLeft, Users, UserCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerGroupList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification, updateAppBadge } = useWebNotification();
  const [groups, setGroups] = useState(() => readInstantCache('customer_groups', user?.id) || []);
  const [loading, setLoading] = useState(() => !readInstantCache('customer_groups', user?.id));
  const groupsScrollRef = useScrollRestore('customer_group_list_scroll', [groups.length]);
  // Issue 16 — customer group unread tracking (badge + notification).
  const prevGroupUnreadRef = useRef({});
  const isFirstGroupLoadRef = useRef(true);

  useEffect(() => {
    const cur = {};
    (groups || []).forEach(g => { cur[g.id] = g.unread_count || 0; });
    updateAppBadge((groups || []).filter(g => (g.unread_count || 0) > 0).length);
    if (isFirstGroupLoadRef.current) {
      isFirstGroupLoadRef.current = false;
      prevGroupUnreadRef.current = cur;
      return;
    }
    const prev = prevGroupUnreadRef.current;
    (groups || []).forEach(g => {
      if ((g.unread_count || 0) > (prev[g.id] || 0)) {
        showNotification({
          title: g.name || 'Group',
          body: 'New group message',
          tag: `customer-group-${g.id}-${Date.now()}`,
          data: { groupId: g.id, isGroup: true },
        });
      }
    });
    prevGroupUnreadRef.current = cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchGroups();

    // BIG-PLAN Point 3 — relaxed 2s → 30s safety-net poll (unread badges
    // arrive via socket events; group_message_updated etc.)
    const interval = setInterval(fetchGroups, 30000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/user-groups?user_id=${user.id}`);
      const _v = response.data || [];
      setGroups(_v);
      writeInstantCache('customer_groups', user?.id, _v);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setLoading(false);
    }
  };

  const openGroupChat = (groupId) => {
    navigate(`/customer/group/${groupId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header - WhatsApp Style */}
      <div className="bg-green-600 text-white p-4 shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/customer-select')}
            className="text-white hover:bg-green-700 p-2 rounded-full transition-colors"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Group Chats</h1>
            <p className="text-sm text-green-100">{groups.length} groups</p>
          </div>
        </div>
      </div>

      {/* Groups List - WhatsApp Style */}
      <div ref={groupsScrollRef} className="flex-1 overflow-y-auto bg-white">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
            <Users size={64} weight="thin" className="mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No Group Chats</p>
            <p className="text-sm text-center">You haven't been added to any groups yet.</p>
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group.id}
              onClick={() => openGroupChat(group.id)}
              className="flex items-center gap-3 p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors active:bg-gray-100"
            >
              {/* Group Icon */}
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Users size={24} weight="fill" className="text-green-600" />
              </div>

              {/* Group Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-gray-800 truncate">{group.name}</h3>
                  {group.last_message_time && (
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {formatTime(group.last_message_time)}
                    </span>
                  )}
                </div>
                
                {/* Last Message Preview */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 truncate">
                    {group.last_message_sender && (
                      <span className="font-medium">{group.last_message_sender}: </span>
                    )}
                    {group.last_message_text || 'No messages yet'}
                  </p>
                  
                  {/* Unread Count Badge - WhatsApp Style */}
                  {group.unread_count > 0 && (
                    <div className="bg-green-600 text-white text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 ml-2">
                      {group.unread_count > 99 ? '99+' : group.unread_count}
                    </div>
                  )}
                </div>

                {/* Members Count */}
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <UserCircle size={14} />
                  <span>{group.member_count || 0} members</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Format timestamp to WhatsApp style (Today, Yesterday, or Date)
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const now = new Date();
  const messageDate = new Date(timestamp);
  const diffDays = Math.floor((now - messageDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Today - show time only
    return messageDate.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return messageDate.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', weekday: 'short' });
  } else {
    return messageDate.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', 
      month: 'short', 
      day: 'numeric' 
    });
  }
};

export default CustomerGroupList;
